import { setUser } from '@sentry/react';
import { addHours, isFuture } from 'date-fns';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Cryptocurrency,
  Currency,
  DeviceWasUpdatedEvent,
  EnabledWallet,
  Sport,
} from '#src/__generated__/globalTypes.ts';
import { createLink } from '#src/atoms/typography/Link/index.tsx';
import { VERSION, isProduction } from '#src/config.ts';
import { SETTINGS_SECURITY } from '#src/constants/__generated__/routes.ts';
import { useConfigContext } from '#src/contexts/config/index.ts';
import { useDeviceFingerprintContext } from '#src/contexts/deviceFingerprint/index.ts';
import { useEventsContext } from '#src/contexts/events/index.ts';
import { useSessionReplayShutdown } from '#src/contexts/events/useSessionReplayShutdown.ts';
import { useGraphqlContext } from '#src/contexts/graphql/index.tsx';
import { useSnackNotificationContext } from '#src/contexts/snackNotification/index.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { idFromObject } from '#src/gql/idFromObject.ts';
import { useIsAuthorizedRouteForSecondFactorRecommendation } from '#src/hooks/isAuthorizedRouteForSecondFactorRecommendation.ts';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import { useSubscription } from '#src/hooks/useSubscription.ts';
import { getBlockchainCardsCount, getRarities } from '#src/lib/cardCounts.ts';
import { isType } from '#src/lib/gql/index.ts';
import { asObject } from '#src/lib/json.ts';
import {
  importantNotificationCategories,
  notificationCategories,
} from '#src/lib/notifications.ts';

import type {
  onDeviceWasUpdated,
  onDeviceWasUpdatedVariables,
} from './__generated__/queries.graphql.ts';
import { CurrentUserContext } from './index.ts';
import { onDeviceSubscription, subscription } from './queries.ts';

type Props = {
  children: ReactNode;
};

/**
 * Provides the current logged in user.
 */
export const CurrentUserProvider = ({ children }: Props) => {
  const { platform } = useIsMobileApp();
  const { setApiKey } = useGraphqlContext();
  const { identify: identifyAnalytics } = useEventsContext();
  const { currentUser, refetch, defaultFiatCurrency, loading } =
    useConfigContext();
  const { sport } = useSportContext();
  const { showNotification } = useSnackNotificationContext();
  const [currentDeviceFingerPrint, setCurrentDeviceFingerPrint] =
    useState<string>();
  const [shouldResubscribe, setShouldResubscribe] = useState(false);
  const { monitoredFlags, identify: identifyFeatureFlagsUser } =
    useFeatureFlags();
  const { deviceFingerprint } = useDeviceFingerprintContext();
  const isAuthorizedRouteForSecondFactorRecommendation =
    useIsAuthorizedRouteForSecondFactorRecommendation();
  const [
    hasShownSecondFactorRecommendation,
    setHasShownSecondFactorRecommendation,
  ] = useState(false);

  useEffect(() => {
    deviceFingerprint()
      .then(v => setCurrentDeviceFingerPrint(v))
      .then(() => setShouldResubscribe(true));
  }, [deviceFingerprint]);

  useSubscription(subscription, {
    skip: !currentUser,
    shouldResubscribe,
    variables: {
      sport,
      notificationCategories,
      importantNotificationCategories,
    },
    onComplete: () => {
      setShouldResubscribe(false);
    },
    context: {
      headers: {
        DEVICE_FINGERPRINT: currentDeviceFingerPrint,
      },
    },
  });

  const currentDeviceConfirmed = currentUser?.confirmedDevice;
  const currentDeviceId = currentUser?.currentDevice?.id;
  useSubscription<onDeviceWasUpdated, onDeviceWasUpdatedVariables>(
    onDeviceSubscription,
    {
      skip: !currentDeviceId || !!currentDeviceConfirmed,
      onData: ({ data: { data } }) => {
        if (data) {
          const { deviceWasUpdated } = data;
          if (
            deviceWasUpdated &&
            deviceWasUpdated.eventType === DeviceWasUpdatedEvent.confirmed &&
            deviceWasUpdated.id === currentDeviceId
          ) {
            showNotification('deviceSuccessfullyConfirmed');
            refetch();
          }
        }
      },
    }
  );

  const blockchainCardsCount = getBlockchainCardsCount(currentUser?.cardCounts);

  useEffect(() => {
    if (currentUser?.id && identifyFeatureFlagsUser) {
      identifyFeatureFlagsUser({
        key: currentUser.id,
        custom: {
          ...asObject(currentUser.featureFlagCustomAttributes),
          webVersion: +VERSION,
          webviewPlatform: platform,
        },
      });
    }
  }, [
    identifyFeatureFlagsUser,
    currentUser?.id,
    currentUser?.featureFlagCustomAttributes,
    platform,
  ]);

  useEffect(() => {
    if (setUser) {
      setUser(
        currentUser
          ? {
              id: idFromObject(currentUser?.id),
              username: currentUser?.slug,
            }
          : null
      );
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser?.apiKey) {
      setApiKey(currentUser?.apiKey);
    }
  }, [currentUser?.apiKey, setApiKey]);

  useSessionReplayShutdown(
    !!currentUser?.anyUserSportProfile?.commonGameOnboarded
  );

  useEffect(() => {
    if (currentUser?.id) {
      identifyAnalytics(idFromObject(currentUser.id)!, {
        created: currentUser?.createdAt,
        feature_flags: monitoredFlags,
        ...(sport === Sport.BASEBALL && {
          mlbOnboarded: currentUser?.anyUserSportProfile?.onboarded,
        }),
      });
    }
  }, [
    currentUser?.id,
    currentUser?.createdAt,
    identifyAnalytics,
    monitoredFlags,
    currentUser?.anyUserSportProfile?.onboarded,
    sport,
  ]);

  const availableBalanceForWithdrawalPositive = currentUser
    ? currentUser.availableBalanceForWithdrawal > 0n
    : false;

  useEffect(() => {
    const isNewUser =
      currentUser?.createdAt && isFuture(addHours(currentUser.createdAt, 2));

    if (
      !currentUser?.otpRequiredForLogin &&
      currentUser?.confirmed &&
      (blockchainCardsCount > 0 || availableBalanceForWithdrawalPositive) &&
      // hide two factor notification for new users (we don't want to distract them during onboarding flow)
      !isNewUser &&
      isProduction
    ) {
      if (
        isAuthorizedRouteForSecondFactorRecommendation &&
        !hasShownSecondFactorRecommendation
      ) {
        setHasShownSecondFactorRecommendation(true);
        showNotification('secondFactorRecommendation', {
          link: createLink(SETTINGS_SECURITY),
        });
      }
    }
  }, [
    currentUser?.id,
    currentUser?.otpRequiredForLogin,
    currentUser?.confirmed,
    currentUser?.createdAt,
    availableBalanceForWithdrawalPositive,
    blockchainCardsCount,
    showNotification,
    isAuthorizedRouteForSecondFactorRecommendation,
    hasShownSecondFactorRecommendation,
    setHasShownSecondFactorRecommendation,
  ]);

  const fiatWalletAccountable = useMemo(() => {
    if (!currentUser) return null;
    return currentUser.myAccounts
      .map(a =>
        isType(a.accountable, 'PrivateFiatWalletAccount') ? a.accountable : null
      )
      .filter(Boolean)?.[0];
  }, [currentUser]);

  const fiatCurrency = useMemo(() => {
    if (fiatWalletAccountable?.publicInfo.currency) {
      return fiatWalletAccountable.publicInfo.currency;
    }
    return currentUser?.userSettings.fiatCurrency || defaultFiatCurrency;
  }, [
    currentUser?.userSettings.fiatCurrency,
    defaultFiatCurrency,
    fiatWalletAccountable,
  ]);

  const cryptocurrency = useMemo(() => {
    if (currentUser?.userSettings.cryptocurrency) {
      return currentUser?.userSettings.cryptocurrency;
    }
    return Cryptocurrency.WEI;
  }, [currentUser?.userSettings.cryptocurrency]);

  const enabledWallets = currentUser?.profile.enabledWallets || undefined;

  const hasMigratedAndSetupWallets = !!enabledWallets;

  const showCryptoWallet = hasMigratedAndSetupWallets
    ? enabledWallets.includes(EnabledWallet.ETH)
    : true;

  const showFiatWallet = !!enabledWallets?.includes(EnabledWallet.FIAT);

  const onlyShowFiatCurrency = showFiatWallet && !showCryptoWallet;

  const emailConfirmationNeeded = !currentUser?.confirmed;
  const currentDeviceConfirmationNeeded = !currentDeviceConfirmed;

  const currency = useMemo(() => {
    if (onlyShowFiatCurrency) return Currency.FIAT;
    return currentUser?.userSettings.currency || Currency.FIAT;
  }, [currentUser?.userSettings.currency, onlyShowFiatCurrency]);

  const displayEth = useMemo(
    () =>
      Boolean(
        currentUser?.depositedEth ||
        (currentUser?.availableBalanceForWithdrawal &&
          currentUser?.availableBalanceForWithdrawal > 0n)
      ),
    [currentUser]
  );

  const isMe = useCallback(
    (arg?: { slug?: string } | string | null) =>
      !!currentUser?.slug &&
      !!(typeof arg === 'string'
        ? arg === currentUser?.slug
        : arg?.slug === currentUser?.slug),
    [currentUser?.slug]
  );

  const rarities = useMemo(() => {
    return getRarities(currentUser?.cardCounts);
  }, [currentUser?.cardCounts]);

  const value = useMemo(
    () => ({
      currentUser,
      currency,
      fiatCurrency,
      cryptocurrency,
      fiatWalletAccountable,
      displayEth,
      refetch,
      loading,
      blockchainCardsCount,
      rarities,
      walletPreferences: {
        enabledWallets,
        showCryptoWallet,
        showFiatWallet,
        hasMigratedAndSetupWallets,
        onlyShowFiatCurrency,
      },
      emailConfirmationNeeded,
      currentDeviceConfirmationNeeded,
      isMe,
      footballUserProfile: isType(
        currentUser?.anyUserSportProfile,
        'FootballUserSportProfile'
      )
        ? currentUser?.anyUserSportProfile
        : null,
    }),
    [
      currentUser,
      currency,
      fiatCurrency,
      cryptocurrency,
      fiatWalletAccountable,
      displayEth,
      refetch,
      loading,
      blockchainCardsCount,
      rarities,
      enabledWallets,
      showCryptoWallet,
      showFiatWallet,
      hasMigratedAndSetupWallets,
      onlyShowFiatCurrency,
      emailConfirmationNeeded,
      currentDeviceConfirmationNeeded,
      isMe,
    ]
  );

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  );
};
